<template>
  <div class="analysis-label-query-form">
    <ElForm inline label-width="70px" @submit.native.prevent class="query-form elementui-common-form-styles" v-highlight>
      <ElFormItem label="素材游戏组" label-width="85px">
        <el-select
          v-model="queryForm.gameGroups"
          multiple
          collapse-tags
          filterable
          clearable
          placeholder="全部"
          @change="onGameGroupChange"
          style="width: 180px"
        >
          <el-option v-for="item in gameGroups" :key="item.id" :label="item.name" :value="item.id"> </el-option>
        </el-select>
      </ElFormItem>

      <ElFormItem label="素材样式">
        <el-cascader
          v-model="queryForm.imageStyles"
          clearable
          filterable
          collapse-tags
          style="width: 180px"
          :props="{ label: 'content', value: 'labelId', multiple: true }"
          :options="imageStyleTreeList"
        >
        </el-cascader>
      </ElFormItem>

      <ElFormItem label="内容标签" label-width="70px">
        <ThrottleSelector
          style="width: 180px"
          v-model="queryForm.contentLabelIds"
          multiple
          collapse-tags
          :list="contentLabels"
          placeholder="请选择内容标签"
          filterable
          clearable
        />
      </ElFormItem>

      <ElFormItem label="3D视频源" label-width="70px">
        <ThrottleSelector
          style="width: 180px"
          v-model="queryForm.threeDLabelIds"
          multiple
          collapse-tags
          :list="threeDLabels"
          placeholder="请选择内容标签"
          filterable
          clearable
        />
      </ElFormItem>

      <ElFormItem label="素材类型" label-width="70px">
        <ElSelect v-model="queryForm.imgTypes" multiple collapse-tags style="width: 150px" clearable>
          <ElOption v-for="item in imageTypes" :key="item.id" :label="item.name" :value="item.id"></ElOption>
        </ElSelect>
      </ElFormItem>

      <ElFormItem label="数据游戏组" label-width="85px">
        <el-select v-model="queryForm.dataGroupIds" multiple collapse-tags filterable clearable placeholder="全部" style="width: 180px">
          <el-option v-for="item in gameGroups" :key="item.id" :label="item.name" :value="item.id"> </el-option>
        </el-select>
      </ElFormItem>

      <ElFormItem label="媒体" label-width="40px">
        <el-select v-model="queryForm.mediaId" clearable placeholder="全部" style="width: 120px">
          <el-option v-for="item in mediaList" :key="item.id" :label="item.name" :value="item.id"> </el-option>
        </el-select>
      </ElFormItem>

      <ElFormItem label="平台" label-width="40px">
        <el-select v-model="queryForm.platform" clearable placeholder="全部" style="width: 90px">
          <el-option v-for="item in platformsData" :key="item.id" :label="item.name" :value="item.id"> </el-option>
        </el-select>
      </ElFormItem>
      <ElFormItem label-width="0px" class="bg-transparent">
        <slot></slot>
        <div class="compare-filter-trigger">
          <ElButton @click="compareFilterModalVisible = true">更多筛选</ElButton>
          <el-tooltip effect="dark" :disabled="compareFilterPreview.length === 0" placement="bottom">
            <div slot="content">
              <p v-for="text in compareFilterPreview" :key="text" style="line-height: 18px">{{ text }}</p>
            </div>
            <el-badge :hidden="compareFilterPreview.length === 0" :value="compareFilterPreview.length">&ensp;</el-badge>
          </el-tooltip>
        </div>
      </ElFormItem>
    </ElForm>

    <AnalysisReportCompareFilter v-model="compareFilter" :visible.sync="compareFilterModalVisible" @confirm="emit('change')" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { initPageData, GAME_GROUPS, MEDIAS, SYS_TAGS, IMAGE_STYLE_TAG_TREE } from '@/data/global-opts'
import { LABEL_TYPES, LEVEL_TYPES } from '@/views/material/tags/const'
import { COMPARE_OPERATOR_OPTIONS, imageTypes } from '../../hooks/useFilter'
import { useLabels } from '@/views/material/uploading/hooks/useLabelRelations'
import ThrottleSelector from '@/components/throttle-select/index.vue'
import AnalysisReportCompareFilter from '../analysis-report-compare-filter.vue'

// eslint-disable-next-line
const emit = defineEmits(['change'])

const PAGE_REQUIRED_OPTS = initPageData([GAME_GROUPS, MEDIAS, SYS_TAGS, IMAGE_STYLE_TAG_TREE])
const platformsData = [
  { id: 1, name: '安卓' },
  { id: 2, name: 'iOS' },
  { id: 3, name: '小程序' }
]

const queryForm = ref({
  contentLabelIds: [],
  threeDLabelIds: [],
  imageStyles: [],
  imgTypes: [],
  dataGroupIds: [],
  gameGroups: [],
  tagGroups: [],
  mediaId: '',
  platform: '',
  amountOperatorType: 5,
  amountValue: undefined,
  newRoleRateOperatorType: 5,
  newRoleRateValue: undefined
})

const selectedMaterialGames = ref([])

const gameGroups = computed(PAGE_REQUIRED_OPTS.GAME_GROUPS)
const mediaList = computed(PAGE_REQUIRED_OPTS.MEDIAS)
const imageStyleTree = computed(PAGE_REQUIRED_OPTS.IMAGE_STYLE_TAG_TREE)

const labelList = computed(PAGE_REQUIRED_OPTS.SYS_TAGS)
const labelEnum = computed(() => {
  return {
    contentLabels: labelList.value.filter((label) => label.labelType === LABEL_TYPES.CONTENT_LABEL),
    threeDLabels: labelList.value.filter((label) => label.labelType === LABEL_TYPES.THREED_LABEL)
  }
})

// 构造一个供useLabels hook 使用的结构，需要包含其中一些必要参数，根据游戏过滤内容标签和3d视频源
const gameFilterForm = computed(() => {
  return { game: selectedMaterialGames.value }
})
const { contentLabelList, threedLabeList, imageStyleTagList } = useLabels(gameFilterForm)

const contentLabels = computed(() => {
  return selectedMaterialGames.value?.length ? contentLabelList.value : labelEnum.value.contentLabels
})
const threeDLabels = computed(() => {
  return selectedMaterialGames.value?.length ? threedLabeList.value : labelEnum.value.threeDLabels
})
const imageStyleTreeList = computed(() => {
  return selectedMaterialGames.value?.length ? imageStyleTagList.value : imageStyleTree.value
})

const compareFilterModalVisible = ref(false)
const compareFilter = computed({
  get: () => ({
    amountOperatorType: queryForm.value.amountOperatorType,
    amountValue: queryForm.value.amountValue,
    newRoleRateOperatorType: queryForm.value.newRoleRateOperatorType,
    newRoleRateValue: queryForm.value.newRoleRateValue
  }),
  set: (value) => Object.assign(queryForm.value, value)
})
const isEmptyCompareValue = (value) => value === undefined || value === null || value === ''
const getCompareOperatorLabel = (operatorType) => COMPARE_OPERATOR_OPTIONS.find((item) => item.value === operatorType)?.label || ''
const compareFilterPreview = computed(() => {
  const texts = []
  if (!isEmptyCompareValue(compareFilter.value.amountValue)) {
    texts.push(`实际消耗${getCompareOperatorLabel(compareFilter.value.amountOperatorType)}${compareFilter.value.amountValue}元`)
  }
  if (!isEmptyCompareValue(compareFilter.value.newRoleRateValue)) {
    texts.push(
      `新创角回款率${getCompareOperatorLabel(compareFilter.value.newRoleRateOperatorType)}${compareFilter.value.newRoleRateValue}%`
    )
  }
  return texts
})

const onGameGroupChange = (vals) => {
  const games = gameGroups.value
    .filter((g) => vals.includes(g.id))
    .map((g) => g.gameIds)
    .flat()
    .map(Number)

  selectedMaterialGames.value = games
  queryForm.value.contentLabelIds = []
  queryForm.value.threeDLabelIds = []
}

// eslint-disable-next-line
defineExpose({
  getForm() {
    return queryForm.value
  },
  setQueryParams(params) {
    queryForm.value = { ...queryForm.value, ...params }
  }
})
</script>

<style scoped lang="scss">
.query-form {
  margin-top: 10px;

  .date-picker-wrapper {
    display: inline-block;
    margin-bottom: 8px !important;
    vertical-align: top;
  }
}

.compare-filter-trigger {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
}
</style>
